import { SiteConfig } from './api';
import { getServerApiUrl } from './urls';

const API_BASE_URL = getServerApiUrl();

// Valeurs par défaut
const defaultConfig: SiteConfig = {
  id: 0,
  companyName: 'CERAD-CI',
  companyFullName: 'Centre de Recherche Action pour le Développement',
  slogan: 'Guider le développement en Afrique par les données',
  description: "Cabinet d'expertise spécialisé en Suivi-Évaluation, RSE et gestion de projets.",
  descriptionLong: null,
  phone: '+225 07 00 00 00 00',
  whatsapp: null,
  email: 'contact@cerad-ci.org',
  address: 'Cocody - Riviera Palmeraie',
  city: 'Abidjan',
  country: "Côte d'Ivoire",
  mapLatitude: 5.3599517,
  mapLongitude: -4.0082563,
  mapZoom: 15,
  openingHours: 'Lun - Ven : 8h00 - 18h00',
  facebook: null,
  linkedin: null,
  twitter: null,
  instagram: null,
  youtube: null,
  tiktok: null,
  yearsExperience: 15,
  projectsCount: 200,
  partnersCount: 50,
  founderName: 'Elisée KOUMAN',
  founderTitle: 'Co Fondateur',
  founderBio: null,
  founderImage: null,
  partners: [],
  siteUrl: 'https://cerad-ci.org',
  apiUrl: 'https://api.cerad-ci.org',
  metaTitle: null,
  metaDescription: null,
  updatedAt: new Date().toISOString(),
};

/**
 * Récupère la configuration du site côté serveur avec cache
 * Utilise revalidation par tag pour être invalidé quand les settings changent
 */
export async function getSiteConfig(): Promise<SiteConfig> {
  try {
    const response = await fetch(`${API_BASE_URL}/settings`, {
      next: {
        tags: ['site-config'],
        revalidate: 0, // Désactiver le cache pour que les modifs en base soient immédiates
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const json = await response.json();
    // Handle the new ApiResponse format { success: true, data: {...} }
    const data = json.success && json.data ? json.data : json;
    return data as SiteConfig;
  } catch (error) {
    // En développement, afficher un message moins alarmant
    // Afficher un warning informatif (backend peut ne pas être disponible lors de la build)
    const code = getErrorCode(error);
    console.warn(`Could not fetch site config from ${API_BASE_URL}/settings — using default config. (${code ?? 'unknown'})`);
    return defaultConfig;
  }
}

/**
 * Récupère uniquement les stats (optimisé)
 */
export async function getSiteStats(): Promise<Pick<SiteConfig, 'yearsExperience' | 'projectsCount' | 'partnersCount'>> {
  try {
    const response = await fetch(`${API_BASE_URL}/settings/stats`, {
      next: {
        tags: ['site-config'],
        revalidate: 0,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const json = await response.json();
    // Handle the new ApiResponse format { success: true, data: {...} }
    const data = json.success && json.data ? json.data : json;
    return data;
  } catch (error) {
    // Ne pas faire échouer la build si le backend n'est pas joignable
    const code = getErrorCode(error);
    console.warn(`Could not fetch site stats from ${API_BASE_URL}/settings/stats — using defaults. (${code ?? 'unknown'})`);
    return {
      yearsExperience: defaultConfig.yearsExperience,
      projectsCount: defaultConfig.projectsCount,
      partnersCount: defaultConfig.partnersCount,
    };
  }
}

/**
 * Récupère uniquement les infos de contact (optimisé)
 */
export async function getSiteContact() {
  try {
    const response = await fetch(`${API_BASE_URL}/settings/contact`, {
      next: {
        tags: ['site-config'],
        revalidate: 0,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const json = await response.json();
    // Handle the new ApiResponse format { success: true, data: {...} }
    const data = json.success && json.data ? json.data : json;
    return data;
  } catch (error) {
    const code = getErrorCode(error);
    console.warn(`Could not fetch site contact from ${API_BASE_URL}/settings/contact — using defaults. (${code ?? 'unknown'})`);
    return {
      phone: defaultConfig.phone,
      email: defaultConfig.email,
      address: defaultConfig.address,
      city: defaultConfig.city,
      country: defaultConfig.country,
      openingHours: defaultConfig.openingHours,
    };
  }
}

function getErrorCode(err: unknown): string | undefined {
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object') {
    const e = err as Record<string, unknown>;
    const cause = e['cause'];
    if (cause && typeof cause === 'object') {
      const c = cause as Record<string, unknown>;
      if (typeof c['code'] === 'string') return c['code'] as string;
    }
    if (typeof e['code'] === 'string') return e['code'] as string;
    if (typeof e['message'] === 'string') return e['message'] as string;
  }
  return undefined;
}

export { defaultConfig };
